import React, { useState } from 'react';
import Swal from 'sweetalert2';
import moment from 'moment';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import axios from 'axios';
import cookie from 'react-cookies';
import { snakeCaseToTitleCase } from '../../../common/utils/api_utils';
import CustomLoader from '../../common/CustomLoader';
import showAlert from '../../../common/utils/alert';
import TooltipInfo from '../../../common/docs/TooltipInfo';
import { description } from '../../../common/utils/event_constants';
import DownloadCodeHistory from './DownloadCodeHistory';

const ExportRequestList = ({
  projectId,
  exportRequestList,
  deleteExportRequest,
  exportProjectCodePending,
  deleteFlag,
  loadDownloadCodeHistory,
  codeDownloadHistories,
  githubExportList,
  settings,
  pushToGitHub,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [exportId, setExportId] = useState('');

  const updateState = (id) => {
    setIsOpen(!isOpen);
    setExportId(id);
  };
  const isExportList = exportRequestList && exportRequestList.length > 0;
  const handleDelete = (id) => {
    const preConfirm = () => {
      return deleteExportRequest({ id, projectId });
    };
    showAlert("You won't be able to revert it!", preConfirm, 'Yes, delete it!');
  };

  // const handleGitHubPush = (id) => {
  //   const preConfirm = () => pushToGitHub({ id, projectId });
  //   showAlert("You won't be able to revert it!", preConfirm, 'Yes, push it!');
  // };
  const [githubPushLoading, setGithubPushLoading] = useState(false);

  const handleGitHubPush = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, push it!',
    });

    if (!result.isConfirmed) return;

    let isResolved = false;
    setGithubPushLoading(true);

    Swal.fire({
      title: 'Pushing to GitHub...',
      html: 'Please wait while we push your code to GitHub.',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      },
    });

    const timeout = setTimeout(() => {
      if (!isResolved) {
        isResolved = true;
        Swal.fire({
          icon: 'info',
          title: 'Push running in background',
          text: 'The GitHub upload is still in progress. Your code will be added to the repository shortly.',
        });
      }
    }, 20000);

    try {
      await pushToGitHub({ id, projectId });
      isResolved = true;
      clearTimeout(timeout);

      Swal.fire({
        icon: 'success',
        title: 'Code pushed to GitHub successfully!',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      isResolved = true;
      clearTimeout(timeout);

      Swal.fire({
        icon: 'error',
        title: 'GitHub Push Failed',
        text: err.response.data.message || 'An unexpected error occurred.',
      });
    } finally {
      setGithubPushLoading(false);
    }
  };

  const downloadCode = (id) => {
    const environment = localStorage.getItem('environment');
    const headers = {
      'x-project-id': projectId,
      token: `Bearer ${cookie.load('token')}`,
    };
    if (environment) {
      headers['x-project-env'] = environment.toLowerCase();
    }
    axios({
      url: `${process.env.REACT_APP_BUILDER_ENGINE}code-export/downloadCode/${id}`,
      method: 'GET',
      responseType: 'blob', // important
      headers,
    }).then((response) => {
      const { data, headers } = response;
      const contentDisposition = headers['content-disposition'];
      const fileName = contentDisposition.substring(
        contentDisposition.indexOf('filename=') + 9,
        contentDisposition.length,
      );

      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();

      link.remove();
      URL.revokeObjectURL(url);
    });
  };

  if (!isExportList) return <div className="alert">No Code Export Request</div>;
  if (exportProjectCodePending)
    return <CustomLoader loading={exportProjectCodePending} size="20px" color="#303453" />;

  const loadHistoryModal = () => {
    if (!exportId) {
      return '';
    }
    return (
      <Modal isOpen={isOpen} toggle={() => setIsOpen(!isOpen)} backdrop="static">
        <ModalHeader toggle={() => setIsOpen(!isOpen)} className="border-bottom">
          <strong>Download History</strong>
        </ModalHeader>
        <ModalBody>
          <DownloadCodeHistory
            projectId={projectId}
            exportId={exportId}
            codeDownloadHistories={codeDownloadHistories}
            loadDownloadCodeHistory={loadDownloadCodeHistory}
          />
        </ModalBody>
      </Modal>
    );
  };

  const showSettingInfo = (settingUuid) => {
    if (!settings || settings.length <= 0) {
      return;
    }
    const setting = settings.find((setting) => setting.uuid === settingUuid);
    if (!setting) {
      return '-';
    }
    return setting.name;
  };
  const showGithubInfo = (githubUuid) => {
    if (!githubExportList || githubExportList.length <= 0) {
      return;
    }
    const github = githubExportList.find((github) => github.uuid === githubUuid);
    if (!github) {
      return '-';
    }
    return `${github.repoName} (${github.repoOwner})`;
  };
  return (
    <>
      <table className="table table-bordered table-word-break">
        <thead>
          <tr>
            <th>#</th>
            <th>Project Name</th>
            <th>Name</th>
            <th>Deployment Setting</th>
            <th>GitHub</th>
            <th>Request Initiated</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {isExportList &&
            exportRequestList.map((listItem, index) => (
              <tr key={listItem._id}>
                <td>{index + 1}</td>
                <td>{listItem.projectName}</td>
                <td>{listItem.displayName}</td>
                <td>{showSettingInfo(listItem.settingUuid)}</td>
                <td>{showGithubInfo(listItem.githubSettingUuid)}</td>
                <td>{moment(listItem.createdAt).format('Do MMM YYYY hh:mm:ss A')}</td>
                <td>{snakeCaseToTitleCase(listItem.status)}</td>
                <td>
                  {listItem.status === 'EXPORTED' && (
                    <>
                      <button
                        type="button"
                        className="btn btn-theme-submit btn-xs"
                        title="Download"
                        onClick={() => downloadCode(listItem.uuid)}
                      >
                        <i className="wb webconnect-download"></i>&nbsp;
                      </button>
                    </>
                  )}
                  &nbsp;
                  {listItem.githubSettingUuid !== undefined && (
                    <>
                      <button
                        type="button"
                        className="btn btn-theme-primary btn-xs"
                        title="Push code to GitHub"
                        onClick={() => handleGitHubPush(listItem.uuid)}
                      >
                        <i className="wb webconnect-github"></i>
                      </button>
                    </>
                  )}
                  &nbsp;
                  <button
                    type="button"
                    className="btn btn-theme-delete btn-xs"
                    style={{ cursor: deleteFlag ? 'pointer' : 'not-allowed' }}
                    title="Delete Item"
                    onClick={deleteFlag ? () => handleDelete(listItem.uuid) : undefined}
                  >
                    <i className="wb webconnect-trash-regular" />
                    &nbsp;
                    {deleteFlag ? null : (
                      <TooltipInfo title={description} showHeader={true} showArrow={true} />
                    )}
                  </button>
                  &nbsp;
                  <button
                    type="button"
                    className="btn btn-theme-warning btn-xs"
                    title="History"
                    onClick={() => updateState(listItem.uuid)}
                  >
                    <i className="wb webconnect-journal-text" />
                  </button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      {loadHistoryModal()}
    </>
  );
};

export default ExportRequestList;
